/**
 * 认证状态模块
 * 管理用户登录、登出、会员信息等
 */

import userApi from '@/api/user'
import memberApi from '@/api/member'

// 定义存储token变量
const TokenKey = 'this_user_token'
// 定义存储account变量
const AccountKey = 'this_user_account'
// 定义存储会员信息变量
const MemberKey = 'this_user_member'

// 定义 state 用于存储数据
const state = {
  token: localStorage.getItem(TokenKey) || '',
  account: JSON.parse(localStorage.getItem(AccountKey)) || {},
  memberInfo: JSON.parse(localStorage.getItem(MemberKey)) || null
}

// 定义 mutations 用于改变 state 的值
const mutations = {
  /**
   * 设置token
   * @param state
   * @param token
   */
  setToken(state, token) {
    state.token = token
  },
  
  /**
   * 清除token
   * @param state
   */
  clearToken(state) {
    state.token = ''
  },
  
  /**
   * 设置用户信息
   * @param state
   * @param account
   */
  setAccount(state, account) {
    state.account = account
  },
  
  /**
   * 清除用户信息
   * @param state
   */
  clearAccount(state) {
    state.account = null
  },
  
  /**
   * 设置会员信息
   * @param state
   * @param memberInfo
   */
  setMemberInfo(state, memberInfo) {
    state.memberInfo = memberInfo
  },
  
  /**
   * 清除会员信息
   * @param state
   */
  clearMemberInfo(state) {
    state.memberInfo = null
  }
}

// 定义 actions 用于处理异步操作
const actions = {
  /**
   * 登录
   * @param commit
   * @param data
   */
  login({ commit }, data) {
    commit('setToken', data.token)
    localStorage.setItem(TokenKey, data.token) // 持久化存储 token
    
    commit('setAccount', data.account)
    localStorage.setItem(AccountKey, JSON.stringify(data.account)) // 持久化存储 账号信息
    
    if (data.memberInfo) {
      commit('setMemberInfo', data.memberInfo)
      localStorage.setItem(MemberKey, JSON.stringify(data.memberInfo)) // 持久化存储 会员信息
    }
  },
  
  /**
   * 刷新token
   * @param commit
   * @param token
   */
  refreshToken({ commit }, token) {
    commit('setToken', token)
    localStorage.setItem(TokenKey, token) // 持久化存储
  },
  
  /**
   * 刷新用户信息
   * @param commit
   * @param account
   */
  refreshAccount({ commit }, account) {
    commit('setAccount', account)
    localStorage.setItem(AccountKey, JSON.stringify(account)) // 持久化存储
  },
  
  /**
   * 刷新会员信息
   * @param commit
   * @param memberInfo
   */
  refreshMemberInfo({ commit }, memberInfo) {
    commit('setMemberInfo', memberInfo)
    localStorage.setItem(MemberKey, JSON.stringify(memberInfo)) // 持久化存储
  },
  
  /**
   * 退出登录
   * @param commit
   */
  logout({ commit }) {
    commit('clearToken')
    localStorage.removeItem(TokenKey) // 清除token
    
    commit('clearAccount')
    localStorage.removeItem(AccountKey) // 清除持久化存储 账号信息
    
    commit('clearMemberInfo')
    localStorage.removeItem(MemberKey) // 清除持久化存储 会员信息
  },
  
  /**
   * 获取用户信息
   * @param commit
   */
  async fetchUserInfo({ commit }) {
    const res = await userApi.getUserinfo()
    if (res && res.code === 2000) {
      commit('setAccount', res.data)
      localStorage.setItem(AccountKey, JSON.stringify(res.data))
    }
    return res
  },
  
  /**
   * 获取会员信息
   * @param commit
   */
  async fetchMemberInfo({ commit }) {
    const res = await memberApi.getMemberInfo()
    if (res && res.code === 2000) {
      commit('setMemberInfo', res.data)
      localStorage.setItem(MemberKey, JSON.stringify(res.data))
    }
    return res
  }
}

// getters 是 store 的计算属性，用于对 state 进行处理，类似于计算属性 computed
// 可以在组件中使用 this.$store.getters['auth/token'] 来获取 token
// 可以在组件中使用 this.$store.getters['auth/isLogin'] 来获取 isLogin
// 可以在组件中使用 this.$store.getters['auth/account'] 来获取 account
const getters = {
  token: state => state.token,
  isLogin: state => !!state.token,
  account: state => state.account,
  memberInfo: state => state.memberInfo,
  
  // 会员类型：1-个人，2-机构，3-理事
  memberType: state => state.memberInfo?.member_type,
  
  // 个人会员等级：0-未付费，1-普通，2-注册
  memberLevel: state => state.memberInfo?.member_level,
  
  // 付费状态：0-未打款，1-已打款
  payStatus: state => state.memberInfo?.pay_status,
  
  // 审核状态：0-待审核，1-已通过，2-已驳回
  auditStatus: state => state.memberInfo?.audit_status,
  
  // 是否个人会员
  isIndividual: state => state.memberInfo?.member_type === 1,
  
  // 是否机构会员
  isOrganization: state => state.memberInfo?.member_type === 2,
  
  // 是否理事会员
  isDirector: state => state.memberInfo?.member_type === 3,
  
  // 是否未付费会员
  isUnpaid: state => state.memberInfo?.member_level === 0,
  
  // 是否普通会员
  isNormal: state => state.memberInfo?.member_level === 1,
  
  // 是否注册会员
  isRegistered: state => state.memberInfo?.member_level === 2,
  
  // 是否已打款/已支付
  isPaid: state => state.memberInfo?.pay_status === 1,
  hasPaid: state => state.memberInfo?.pay_status === 1,
  
  // 是否审核通过
  isApproved: state => state.memberInfo?.audit_status === 1,
  
  // 是否待审核
  isPending: state => state.memberInfo?.audit_status === 0,
  
  // 是否已驳回
  isRejected: state => state.memberInfo?.audit_status === 2
}

export default {
  namespaced: true, // 防止命名冲突
  state,
  mutations,
  actions,
  getters
}
